import React from "react";
import { X, AlertCircle, CheckCircle, HelpCircle } from "lucide-react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message?: string;
  type?: "success" | "error" | "warning" | "info" | "confirm" | "custom";
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  children?: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  type = "info",
  onConfirm,
  confirmText = "تایید",
  cancelText = "انصراف",
  isLoading = false,
  children,
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case "error":
        return <AlertCircle className="text-red-500" size={32} />;
      case "success":
        return <CheckCircle className="text-emerald-500" size={32} />;
      case "warning":
        return <AlertCircle className="text-orange-500" size={32} />;
      case "confirm":
        return <HelpCircle className="text-blue-500" size={32} />;
      case "custom":
        return null;
      default:
        return <AlertCircle className="text-blue-500" size={32} />;
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-2xl p-6 border border-gray-100 dark:border-slate-800 scale-100 animate-in zoom-in-95 duration-200">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            {type !== "custom" && (
              <div className={`p-2 rounded-xl bg-gray-50 dark:bg-slate-800`}>
                {getIcon()}
              </div>
            )}
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {children ? (
          <div className="mb-8">{children}</div>
        ) : message ? (
          <p className="text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
            {message}
          </p>
        ) : null}

        <div className="flex gap-3 justify-end">
          {type === "custom" ? null : type === "confirm" ? (
            <>
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2.5 rounded-xl text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                disabled={isLoading}
                className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg shadow-blue-600/20 transition-all disabled:opacity-70 disabled:cursor-wait flex items-center gap-2"
              >
                {isLoading ? "در حال پردازش..." : confirmText}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="w-full px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white font-bold hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
            >
              متوجه شدم
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
